"use client";

import { useState, useEffect, useLayoutEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/hooks/useAuth";
import { isMemberDashboardRoute } from "@/lib/member-dashboard-route";

export default function Nav() {
  const pathname = usePathname();
  const [windowDash, setWindowDash] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const { user, loading, isActiveMember, isAdmin } = useAuth();

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    setWindowDash(isMemberDashboardRoute(window.location.pathname));
  }, [pathname]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && mobileOpen) setMobileOpen(false);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mobileOpen]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  if (isMemberDashboardRoute(pathname) || windowDash) {
    return null;
  }

  return (
    <>
    <nav
      style={{
        position: "fixed",
        top: "var(--banner-height, 0px)",
        left: 0,
        right: 0,
        zIndex: 60,
        backgroundColor: "rgba(0, 0, 0, 0.92)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
      }}
      aria-label="Main navigation"
    >
      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "0 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "64px",
        }}
      >
        {/* Logo */}
        <Link href="/" style={{ display: "flex", alignItems: "center" }}>
          <img src="/logo-nav.svg" alt="704 Collective" width={56} height={56} className="h-14 w-14" />
        </Link>

        {/* Desktop Links */}
        <div style={{ display: "flex", alignItems: "center", gap: "32px" }} className="desktop-nav">
          {/* Membership Dropdown */}
          <div
            style={{ position: "relative" }}
            onMouseEnter={() => setMembershipOpen(true)}
            onMouseLeave={() => setMembershipOpen(false)}
          >
            <button
              className="nav-link"
              aria-expanded={membershipOpen}
              aria-haspopup="true"
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontFamily: "inherit", fontSize: "0.875rem",
                display: "flex", alignItems: "center", gap: "4px",
                minHeight: "44px",
              }}
            >
              Membership
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true"
                style={{ transform: membershipOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 200ms ease" }}>
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {membershipOpen && (
              <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", paddingTop: "8px" }}>
                <div role="menu" style={{
                  backgroundColor: "#1A1A1A",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  borderRadius: "12px", padding: "8px",
                  minWidth: "200px",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
                }}>
                  {/* EXCEPTION: stays as /social */}
                  <Link href="/social" role="menuitem"
                    style={{ display: "block", padding: "10px 16px", borderRadius: "8px", fontSize: "0.875rem", color: "rgba(255, 255, 255, 0.7)", textDecoration: "none", transition: "all 200ms ease" }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.06)"; e.currentTarget.style.color = "#FFFFFF"; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)"; }}>
                    <span style={{ fontWeight: 600 }}>704 Social</span>
                    <span style={{ display: "block", fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.4)", marginTop: "2px" }}>Events, wellness &amp; community</span>
                  </Link>
                  <Link href="/business" role="menuitem"
                    style={{ display: "block", padding: "10px 16px", borderRadius: "8px", fontSize: "0.875rem", color: "rgba(255, 255, 255, 0.7)", textDecoration: "none", transition: "all 200ms ease" }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.06)"; e.currentTarget.style.color = "#FFFFFF"; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)"; }}>
                    <span style={{ fontWeight: 600 }}>704 Business</span>
                    <span style={{ display: "block", fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.4)", marginTop: "2px" }}>Strategic networking &amp; growth</span>
                  </Link>
                  <Link href="/partners" role="menuitem"
                    style={{ display: "block", padding: "10px 16px", borderRadius: "8px", fontSize: "0.875rem", color: "rgba(255, 255, 255, 0.7)", textDecoration: "none", transition: "all 200ms ease" }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.06)"; e.currentTarget.style.color = "#FFFFFF"; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)"; }}>
                    <span style={{ fontWeight: 600 }}>Partners</span>
                    <span style={{ display: "block", fontSize: "0.75rem", color: "rgba(255, 255, 255, 0.4)", marginTop: "2px" }}>Directory, tiers &amp; collaborations</span>
                  </Link>
                </div>
              </div>
            )}
          </div>

          <Link href="/events" className="nav-link">Events</Link>
          <Link href="/partners" className="nav-link">Partners</Link>
          <Link href="/blog" className="nav-link">Blog</Link>

          {/* Desktop Auth CTAs */}
          {loading ? (
            <div style={{ width: "120px" }} />
          ) : user ? (
            <Link href="/dashboard"
              style={{ backgroundColor: "#FFFFFF", color: "#000000", fontWeight: 600, fontSize: "0.8125rem", padding: "10px 24px", borderRadius: "8px", textDecoration: "none", letterSpacing: "0.02em", transition: "all 200ms ease" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(255, 255, 255, 0.15)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
              Dashboard
            </Link>
          ) : (
            <>
              <Link href="/join"
                style={{ backgroundColor: "#FFFFFF", color: "#000000", fontWeight: 600, fontSize: "0.8125rem", padding: "10px 24px", borderRadius: "8px", textDecoration: "none", letterSpacing: "0.02em", transition: "all 200ms ease" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(255, 255, 255, 0.15)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                Join Now
              </Link>
              <Link href="/login"
                style={{ backgroundColor: "transparent", color: "#FFFFFF", fontWeight: 600, fontSize: "0.8125rem", padding: "10px 24px", borderRadius: "8px", textDecoration: "none", letterSpacing: "0.02em", transition: "all 200ms ease", border: "1px solid rgba(255, 255, 255, 0.25)" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.5)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(255, 255, 255, 0.1)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.25)"; e.currentTarget.style.boxShadow = "none"; }}>
                Login
              </Link>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="mobile-nav-toggle"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          style={{
            flexDirection: "column", gap: "5px",
            padding: "10px", margin: "-10px",
            background: "none", border: "none", cursor: "pointer",
            minWidth: "44px", minHeight: "44px",
            alignItems: "center", justifyContent: "center",
          }}
        >
          <span aria-hidden="true" style={{ display: "block", width: "22px", height: "2px", backgroundColor: "#FFFFFF", transition: "all 200ms ease", transform: mobileOpen ? "rotate(45deg) translateY(7px)" : "none" }} />
          <span aria-hidden="true" style={{ display: "block", width: "22px", height: "2px", backgroundColor: "#FFFFFF", transition: "all 200ms ease", opacity: mobileOpen ? 0 : 1 }} />
          <span aria-hidden="true" style={{ display: "block", width: "22px", height: "2px", backgroundColor: "#FFFFFF", transition: "all 200ms ease", transform: mobileOpen ? "rotate(-45deg) translateY(-7px)" : "none" }} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div id="mobile-menu" role="menu" className="mobile-nav-menu"
          style={{
            backgroundColor: "#000000",
            borderTop: "1px solid rgba(255, 255, 255, 0.06)",
            padding: "24px 20px",
            maxHeight: "calc(100dvh - 64px)",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>

            {/* Membership group - EXCEPTION: 704 Social stays as /social */}
            <div style={{ padding: "16px 0", borderBottom: "1px solid rgba(255, 255, 255, 0.06)" }}>
              <span style={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "0.6875rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                Membership
              </span>
              <Link href="/social" role="menuitem" onClick={() => setMobileOpen(false)}
                style={{ display: "flex", alignItems: "center", color: "rgba(255, 255, 255, 0.7)", textDecoration: "none", padding: "12px 0 4px 12px", fontSize: "1rem", minHeight: "48px" }}>
                704 Social
              </Link>
              <Link href="/business" role="menuitem" onClick={() => setMobileOpen(false)}
                style={{ display: "flex", alignItems: "center", color: "rgba(255, 255, 255, 0.7)", textDecoration: "none", padding: "4px 0 4px 12px", fontSize: "1rem", minHeight: "48px" }}>
                704 Business
              </Link>
              <Link href="/partners" role="menuitem" onClick={() => setMobileOpen(false)}
                style={{ display: "flex", alignItems: "center", color: "rgba(255, 255, 255, 0.7)", textDecoration: "none", padding: "4px 0 4px 12px", fontSize: "1rem", minHeight: "48px" }}>
                Partners
              </Link>
            </div>

            <Link href="/events" role="menuitem" onClick={() => setMobileOpen(false)}
              style={{ color: "rgba(255, 255, 255, 0.7)", textDecoration: "none", padding: "16px 0", fontSize: "1rem", borderBottom: "1px solid rgba(255, 255, 255, 0.06)", display: "flex", alignItems: "center", minHeight: "56px" }}>
              Events
            </Link>
            <Link href="/blog" role="menuitem" onClick={() => setMobileOpen(false)}
              style={{ color: "rgba(255, 255, 255, 0.7)", textDecoration: "none", padding: "16px 0", fontSize: "1rem", borderBottom: "1px solid rgba(255, 255, 255, 0.06)", display: "flex", alignItems: "center", minHeight: "56px" }}>
              Blog
            </Link>

            <div style={{ paddingTop: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              {user ? (
                <Link href="/dashboard" onClick={() => setMobileOpen(false)}
                  style={{ backgroundColor: "#FFFFFF", color: "#000000", fontWeight: 600, fontSize: "0.9375rem", padding: "14px 24px", borderRadius: "8px", textDecoration: "none", textAlign: "center", display: "block" }}>
                  Go to Dashboard
                </Link>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <Link href="/join" onClick={() => setMobileOpen(false)}
                    style={{ backgroundColor: "#FFFFFF", color: "#000000", fontWeight: 600, fontSize: "0.9375rem", padding: "14px 24px", borderRadius: "8px", textDecoration: "none", textAlign: "center", display: "block" }}>
                    Join Now
                  </Link>
                  <Link href="/login" onClick={() => setMobileOpen(false)}
                    style={{ backgroundColor: "transparent", color: "#FFFFFF", fontWeight: 600, fontSize: "0.9375rem", padding: "14px 24px", borderRadius: "8px", textDecoration: "none", textAlign: "center", display: "block", border: "1px solid rgba(255, 255, 255, 0.25)" }}>
                    Login
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
    </>
  );
}
